import React from 'react';
import { Card } from '../components/ui/card';
import { 
  CheckCircle, 
  MessageCircle, 
  FileText, 
  DollarSign, 
  Calendar, 
  AlertCircle,
  TrendingUp,
  Users
} from 'lucide-react';
import { howItWorksSteps, loanTerms, faqs } from '../mockData';
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

export const HowItWorks = () => {
  const stepIcons = [MessageCircle, FileText, DollarSign, Calendar];

  return (
    <div className="min-h-screen pt-32 pb-20 px-4">
      {/* Hero Section */}
      <section className="container mx-auto mb-20 text-center">
        <div className="max-w-4xl mx-auto space-y-6">
          <h1 className="text-5xl md:text-6xl font-bold leading-tight">
            <span className="text-slate-900">¿Cómo funciona</span>
            <br />
            <span className="bg-gradient-to-r from-amber-500 to-emerald-500 bg-clip-text text-transparent">
              PrestaNet?
            </span>
          </h1>
          <p className="text-xl text-slate-600 leading-relaxed">
            Obtener tu crédito es más fácil de lo que piensas. Solo 4 pasos simples te separan de tu préstamo.
          </p>
        </div>
      </section>

      {/* Steps Section */}
      <section className="container mx-auto mb-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {howItWorksSteps.map((step, index) => {
            const Icon = stepIcons[index];
            return (
              <Card 
                key={step.step} 
                className="p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-2 border-transparent hover:border-amber-200 relative overflow-hidden group"
              >
                <div className="absolute top-0 right-0 text-8xl font-bold text-amber-100 opacity-50 -mr-4 -mt-4 group-hover:text-amber-200 transition-colors">
                  {step.step}
                </div>
                <div className="relative z-10">
                  <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-emerald-400 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Icon className="text-white" size={28} />
                  </div>
                  <div className="text-sm font-semibold text-amber-600 mb-2">PASO {step.step}</div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{step.title}</h3>
                  <p className="text-slate-600">{step.description}</p>
                </div>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Loan Terms Section */}
      <section className="container mx-auto mb-20">
        <Card className="p-8 md:p-12 bg-gradient-to-br from-slate-900 to-slate-800 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500 rounded-full blur-3xl opacity-10"></div>
          <div className="relative z-10">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Condiciones del Préstamo
              </h2>
              <p className="text-lg text-slate-300">
                Todo lo que necesitas saber sobre nuestros créditos
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-emerald-400 rounded-lg flex items-center justify-center mb-4">
                  <Calendar className="text-white" size={24} />
                </div>
                <div className="text-sm text-slate-300 mb-1">Plazo</div>
                <div className="text-3xl font-bold mb-2">{loanTerms.weeks} semanas</div>
                <p className="text-sm text-slate-400">Pagos semanales cómodos</p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-emerald-400 rounded-lg flex items-center justify-center mb-4">
                  <TrendingUp className="text-white" size={24} />
                </div>
                <div className="text-sm text-slate-300 mb-1">Tasa de Interés</div>
                <div className="text-3xl font-bold mb-2">{loanTerms.interestRate}%</div>
                <p className="text-sm text-slate-400">Sobre el monto total</p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-emerald-400 rounded-lg flex items-center justify-center mb-4">
                  <DollarSign className="text-white" size={24} />
                </div>
                <div className="text-sm text-slate-300 mb-1">Cuota de Apertura</div>
                <div className="text-3xl font-bold mb-2">{loanTerms.openingFee}%</div>
                <p className="text-sm text-slate-400">Solo primer crédito</p>
              </div>
            </div>
          </div>
        </Card>
      </section>

      {/* Special Conditions */}
      <section className="container mx-auto mb-20">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8 text-center">
            Condiciones Especiales
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            <Card className="p-6 border-2 border-emerald-200 bg-emerald-50">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-emerald-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="text-white" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">
                    Cuota de Apertura
                  </h3>
                  <p className="text-slate-700 leading-relaxed">
                    El primer crédito tiene una cuota de apertura del 5%. Esta cuota <strong>no se vuelve a cobrar</strong> a menos que el cliente falle una vez en sus pagos.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-6 border-2 border-amber-200 bg-amber-50">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="text-white" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">
                    Política de Faltas
                  </h3>
                  <p className="text-slate-700 leading-relaxed">
                    Si el cliente falla <strong>3 veces</strong> en sus pagos semanales, se agregará <strong>1 semana más</strong> al plazo, extendiendo el préstamo a 15 semanas en total.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Example Section */}
      <section className="container mx-auto mb-20">
        <Card className="p-8 md:p-12 bg-gradient-to-br from-amber-50 to-emerald-50 border-2 border-amber-100">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-center mb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_f35f5b38-6d8d-497e-9765-fd0e7def8902/artifacts/hh17c75d_ChatGPT%20Image%2015%20ene%202026%2C%2009_16_06%20p.m..png" 
                alt="Net" 
                className="w-24 h-24"
              />
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-6 text-center">
              Ejemplo de Préstamo
            </h2>
            <div className="space-y-4 bg-white rounded-xl p-6 shadow-md">
              <div className="flex justify-between items-center pb-3 border-b">
                <span className="text-slate-600">Monto del préstamo:</span>
                <span className="text-xl font-bold text-slate-900">$5,000</span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b">
                <span className="text-slate-600">Cuota de apertura (5%):</span>
                <span className="text-xl font-bold text-amber-600">$250</span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b">
                <span className="text-slate-600">Intereses (40%):</span>
                <span className="text-xl font-bold text-blue-600">$2,100</span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b border-slate-300">
                <span className="text-slate-600 font-semibold">Total a pagar:</span>
                <span className="text-2xl font-bold text-slate-900">$7,350</span>
              </div>
              <div className="flex justify-between items-center pt-2 bg-gradient-to-r from-amber-100 to-emerald-100 p-4 rounded-lg">
                <span className="text-slate-900 font-bold">Pago semanal (14 semanas):</span>
                <span className="text-3xl font-bold bg-gradient-to-r from-amber-600 to-emerald-600 bg-clip-text text-transparent">$525</span>
              </div>
            </div>
          </div>
        </Card>
      </section>

      {/* FAQs Section */}
      <section className="container mx-auto">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8 text-center">
            Preguntas Frecuentes
          </h2>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-white border-2 border-slate-200 rounded-xl px-6 hover:border-amber-300 transition-colors"
              >
                <AccordionTrigger className="text-left hover:text-amber-600 py-5">
                  <span className="font-semibold text-lg">{faq.question}</span>
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pb-5 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;
